import { useState, useEffect, useCallback } from 'react';
import { Job } from "@/components/jobs/types";
import { useUpdateJob } from '@/hooks/mutations/useUpdateJob';
import { toast } from 'sonner';

// Define JobFormData type (can be co-located or imported if shared)
export type JobFormData = Pick<Job, 'title' | 'company' | 'location' | 'status' | 'description' | 'requirements' | 'source_url' | 'employment_type'> & {
  skills: string;
  experience_required: string | number;
  salary_min: string | number;
  salary_max: string | number;
};

export const initialJobFormData = (job: Job): JobFormData => ({
  title: job.title || '',
  company: job.company || '',
  location: job.location || '',
  employment_type: job.employment_type,
  salary_min: job.salary_min ?? '',
  salary_max: job.salary_max ?? '',
  status: job.status || 'open',
  description: job.description || '',
  requirements: job.requirements || '',
  source_url: job.source_url || '',
  skills: job.skills?.join(', ') || '',
  experience_required: job.experience_required ?? '',
});

interface UseJobOverviewFormParams {
  job: Job;
}

export const useJobOverviewForm = ({ job }: UseJobOverviewFormParams) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<JobFormData>(initialJobFormData(job));
  const { mutate: updateJob, isPending: isUpdating } = useUpdateJob();

  useEffect(() => {
    setFormData(initialJobFormData(job));
    // setIsEditing(false); // Keep existing edit state if job re-fetches while editing. Reset on explicit cancel or save.
  }, [job]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectChange = useCallback((name: keyof JobFormData, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value as any })); // Cast as any for broader compatibility with select values
  }, []);

  const handleSave = useCallback(() => {
    if (!job.id) return;

    const skillsArray = formData.skills.split(',').map(s => s.trim()).filter(s => s !== '');
    const experienceRequired = formData.experience_required === '' ? null : Number(formData.experience_required);
    const salaryMin = formData.salary_min === '' ? null : Number(formData.salary_min);
    const salaryMax = formData.salary_max === '' ? null : Number(formData.salary_max);

    if (formData.experience_required !== '' && isNaN(experienceRequired as number)) {
        toast.error("Experience required must be a number or empty.");
        return;
    }
    if (formData.salary_min !== '' && isNaN(salaryMin as number)) {
        toast.error("Minimum salary must be a number or empty.");
        return;
    }
    if (formData.salary_max !== '' && isNaN(salaryMax as number)) {
        toast.error("Maximum salary must be a number or empty.");
        return;
    }

    if (salaryMin !== null && salaryMax !== null && salaryMin > salaryMax) {
      toast.error("Minimum salary cannot be greater than maximum salary.");
      return;
    }
    
    const { ...dataToUpdate } = formData;

    const updatedData: Partial<Job> = {
      ...dataToUpdate,
      skills: skillsArray.length > 0 ? skillsArray : null,
      experience_required: experienceRequired,
      salary_min: salaryMin,
      salary_max: salaryMax,
    };
    // @ts-ignore Ensure salary string is not part of the update if salary_min/max are used
    delete updatedData.salary; 
    // @ts-ignore skills is already part of formData spread, but we ensure it's the processed array or null
    updatedData.skills = skillsArray.length > 0 ? skillsArray : null;


    updateJob({ id: job.id, data: updatedData }, {
      onSuccess: () => {
        setIsEditing(false);
        toast.success('Job details updated successfully!'); // Toast moved here from useUpdateJob for better UX flow
      },
      // onError remains in useUpdateJob hook for global handling
    });
  }, [job.id, formData, updateJob]);

  const handleCancel = useCallback(() => {
    setFormData(initialJobFormData(job));
    setIsEditing(false);
  }, [job]);

  const formatDate = useCallback((dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  return {
    isEditing,
    setIsEditing,
    formData,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleCancel,
    isUpdating,
    formatDate,
  };
};
